import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import AnalyticsCard from './AnalyticsCard';
import AIInsightsCard from '../insights/AIInsightsCard';
import StreakCard from './StreakCard';
import GlowTrendWidget from './GlowTrendWidget';
import ProductInsightsCard from './ProductInsightsCard';
import SettingsOverlay from './SettingsViews';
import * as FiIcons from 'react-icons/fi';
import { useSubscription } from '../../context/SubscriptionContext';

const { FiEdit2, FiDroplet, FiActivity, FiAward, FiChevronRight, FiCheck, FiCamera, FiLogOut, FiZap } = FiIcons;

const SKIN_TYPES = ['Normal', 'Dry', 'Oily', 'Combination', 'Sensitive'];
const SKIN_CONCERNS = ['Acne', 'Aging', 'Hyperpigmentation', 'Redness', 'Dryness', 'Pores', 'Dullness', 'Fine Lines'];
const GOALS = ['Glass Skin', 'Clear Skin', 'Even Tone', 'Anti-Aging', 'Hydration', 'Glow'];

function StatCard({ value, label, icon, color }) {
  return (
    <div className="bg-white rounded-3xl p-3 py-4 border border-stone-100 shadow-sm flex flex-col items-center gap-1 flex-1 min-w-0">
      <SafeIcon icon={icon} className={`text-xl ${color} flex-shrink-0`} />
      <span className="font-serif text-2xl text-stone-900 truncate w-full text-center leading-none mt-1">{value}</span>
      <span className="font-sans text-xs text-stone-400 text-center leading-tight truncate w-full mt-0.5">{label}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <h3 className="font-sans text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function ProfileView({ entries = [], onLogout, onOpenPricing }) {
  const { plan } = useSubscription();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeSetting, setActiveSetting] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [profile, setProfile] = useState({
    name: localStorage.getItem('glowlytix_display_name') || 'Sophia',
    handle: '@glowlytix',
    avatar: 'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?q=80&w=400&auto=format&fit=crop',
    skinType: 'Combination',
    concerns: ['Hyperpigmentation', 'Dullness'],
    goals: ['Glass Skin', 'Even Tone'],
    waterGoal: 64,
    bio: 'On a mission for glass skin ✦ Tracking everything.',
  });

  const avgGlow = entries.length ? (entries.reduce((s, e) => s + e.glowScore, 0) / entries.length).toFixed(1) : '—';
  const streak = entries.length;
  const avgWater = entries.length ? Math.round(entries.reduce((s, e) => s + (e.water || 0), 0) / entries.length) : 0;

  const toggleConcern = (c) => setProfile(p => ({ ...p, concerns: p.concerns.includes(c) ? p.concerns.filter(x => x !== c) : [...p.concerns, c] }));
  const toggleGoal = (g) => setProfile(p => ({ ...p, goals: p.goals.includes(g) ? p.goals.filter(x => x !== g) : [...p.goals, g] }));
  const handleSave = () => {
    if (profile.name) localStorage.setItem('glowlytix_display_name', profile.name);
    setSaved(true);
    setEditing(false);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="h-full relative flex flex-col bg-stone-50 overflow-hidden">
      <div className="flex-1 overflow-y-auto pb-4">
        {/* Hero */}
        <div className="relative pt-10 pb-10 px-5 flex flex-col items-center text-center"
          style={{ background: 'linear-gradient(160deg, #f5ede8 0%, #fdf6ec 100%)' }}>

          <div className="absolute top-5 left-5 flex items-center gap-2">
            <img
              src="https://media-manager-c.questera.ai/greta-media/1593c15ad88356866a2eabcebc8aef2c67d9fc5afee8d56439ced245a0c6c3417efffbd95aedd1b6f68107af9f17a1fd/images/aW1hZ2UvcG5n/44d2d46ca6ce6a8943aba646038b0408.png"
              alt="Glowlytix Logo"
              className="w-9 h-9 object-contain drop-shadow-sm"
            />
            <span className="font-serif text-lg text-stone-700 leading-none">Glowlytix</span>
          </div>

          {/* Logout button top right */}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/60 border border-stone-200 flex items-center justify-center text-stone-400 active:bg-white transition-colors"
          >
            <SafeIcon icon={FiLogOut} className="text-base" />
          </button>

          <div className="relative mb-3 mt-8">
            <img src={profile.avatar} alt="avatar" className="w-24 h-24 rounded-full object-cover object-top border-4 border-white shadow-md" />
            {editing && (
              <button className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-stone-900 flex items-center justify-center shadow">
                <SafeIcon icon={FiCamera} className="text-white text-xs" />
              </button>
            )}
          </div>

          {editing ? (
            <input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
              className="font-serif text-2xl text-stone-900 text-center bg-transparent border-b border-stone-300 focus:outline-none mb-1 w-40" />
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="font-serif text-3xl text-stone-900">{profile.name}</h1>
              <span
                className="font-sans text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0"
                style={{ background: `${plan.accent}20`, color: plan.accent }}
              >
                {plan.name}
              </span>
            </div>
          )}

          <p className="font-sans text-xs text-stone-400 mt-0.5">{profile.handle}</p>

          {editing ? (
            <textarea value={profile.bio} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))} rows={2}
              className="mt-2 font-sans text-sm text-stone-500 text-center bg-white/60 rounded-2xl px-3 py-2 focus:outline-none w-full resize-none border border-stone-200" />
          ) : (
            <p className="font-sans text-sm text-stone-500 mt-2 max-w-xs leading-relaxed">{profile.bio}</p>
          )}

          <button onClick={() => editing ? handleSave() : setEditing(true)}
            className="mt-4 flex items-center gap-2 px-5 py-2 rounded-full font-sans text-sm font-medium shadow-sm active:opacity-80 transition-opacity text-white"
            style={{ background: editing ? '#5C8A78' : 'linear-gradient(135deg, #C07A52, #E8C97A)' }}>
            <SafeIcon icon={editing ? FiCheck : FiEdit2} className="text-sm" />
            {editing ? (saved ? 'Saved!' : 'Save Profile') : 'Edit Profile'}
          </button>
        </div>

        {/* Stats */}
        <div className="px-5 -mt-6 mb-6 relative z-10 w-full">
          <div className="flex gap-2 w-full">
            <StatCard value={avgGlow} label="Avg Glow" icon={FiAward} color="text-amber-400" />
            <StatCard value={streak} label="Entries" icon={FiActivity} color="text-rose-400" />
            <StatCard value={`${avgWater}oz`} label="Avg Water" icon={FiDroplet} color="text-blue-400" />
          </div>
        </div>

        <div className="px-5">
          <Section title="Active Streaks">
            <StreakCard entries={entries} />
          </Section>

          <Section title="7-Day Snapshot">
            <GlowTrendWidget entries={entries} />
          </Section>

          <Section title="Glow Analytics"><AnalyticsCard entries={entries} /></Section>

          <Section title="AI Skin Insights">
            <AIInsightsCard entries={entries} onOpenPricing={onOpenPricing} />
          </Section>

          <Section title="Product Impact">
            <ProductInsightsCard entries={entries} />
          </Section>

          <Section title="Skin Type">
            <div className="flex flex-wrap gap-2">
              {SKIN_TYPES.map(t => (
                <button key={t} onClick={() => editing && setProfile(p => ({ ...p, skinType: t }))}
                  className={`px-4 py-2 rounded-full border text-sm font-sans transition-all ${profile.skinType === t ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200'} ${editing ? 'active:scale-95' : 'cursor-default'}`}>
                  {profile.skinType === t && '✓ '}{t}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Skin Concerns">
            <div className="flex flex-wrap gap-2">
              {SKIN_CONCERNS.map(c => {
                const active = profile.concerns.includes(c);
                return (
                  <button key={c} onClick={() => editing && toggleConcern(c)}
                    className={`px-4 py-2 rounded-full border text-sm font-sans transition-all ${active ? 'bg-rose-50 text-rose-500 border-rose-200' : 'bg-white text-stone-400 border-stone-200'} ${editing ? 'active:scale-95' : 'cursor-default'}`}>
                    {active && '✓ '}{c}
                  </button>
                );
              })}
            </div>
          </Section>

          <Section title="My Goals">
            <div className="flex flex-wrap gap-2">
              {GOALS.map(g => {
                const active = profile.goals.includes(g);
                return (
                  <button key={g} onClick={() => editing && toggleGoal(g)}
                    className={`px-4 py-2 rounded-full border text-sm font-sans transition-all ${active ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-white text-stone-400 border-stone-200'} ${editing ? 'active:scale-95' : 'cursor-default'}`}>
                    {active && '✦ '}{g}
                  </button>
                );
              })}
            </div>
          </Section>

          <Section title="Daily Water Goal">
            <div className="bg-white rounded-3xl p-5 border border-stone-100 shadow-sm">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiDroplet} className="text-blue-400 text-lg" />
                  <span className="font-sans text-sm text-stone-700 font-medium">Target</span>
                </div>
                <span className="font-serif text-2xl text-blue-400">{profile.waterGoal}oz</span>
              </div>
              {editing && (
                <input type="range" min={32} max={128} step={8} value={profile.waterGoal}
                  onChange={e => setProfile(p => ({ ...p, waterGoal: parseInt(e.target.value) }))} className="w-full" />
              )}
              <p className="font-sans text-xs text-stone-400 mt-2">≈ {Math.round(profile.waterGoal / 16)} bottles of water per day</p>
            </div>
          </Section>

          <Section title="Settings">
            <div className="bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden">
              {/* Subscription row — highlighted */}
              <button
                onClick={() => setActiveSetting('Subscription & Billing')}
                className="w-full flex items-center justify-between px-5 py-4 border-b border-stone-50 active:bg-stone-50 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <SafeIcon icon={FiZap} className="text-base" style={{ color: plan.accent }} />
                  <span className="font-sans text-sm text-stone-700 font-medium">Subscription & Billing</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="font-sans text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ background: `${plan.accent}15`, color: plan.accent }}
                  >
                    {plan.name}
                  </span>
                  <SafeIcon icon={FiChevronRight} className="text-stone-300 text-lg" />
                </div>
              </button>
              {['Reminders', 'Export Data', 'Privacy'].map((item, i, arr) => (
                <button
                  key={item}
                  onClick={() => setActiveSetting(item)}
                  className={`w-full flex items-center justify-between px-5 py-4 active:bg-stone-50 transition-colors ${i < arr.length - 1 ? 'border-b border-stone-50' : ''}`}
                >
                  <span className="font-sans text-sm text-stone-700">{item}</span>
                  <SafeIcon icon={FiChevronRight} className="text-stone-300 text-lg" />
                </button>
              ))}
            </div>
          </Section>

          {/* Sign Out Button */}
          <div className="mb-4">
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full py-4 rounded-2xl font-sans font-medium text-stone-500 bg-white border border-stone-200 flex items-center justify-center gap-2 active:bg-stone-50 transition-colors shadow-sm"
            >
              <SafeIcon icon={FiLogOut} className="text-stone-400" />
              Sign Out
            </button>
          </div>

          <p className="text-center font-sans text-xs text-stone-300 mt-2 mb-4">Glowlytix v1.0 ✦</p>
        </div>
      </div>

      {/* Settings Overlay */}
      <SettingsOverlay
        activeSetting={activeSetting}
        onClose={() => setActiveSetting(null)}
        onOpenPricing={onOpenPricing}
        entries={entries}
      />

      {/* Logout Confirm Modal */}
      {showLogoutConfirm && (
        <div className="absolute inset-0 z-50 flex items-end justify-center bg-black/30 backdrop-blur-sm" onClick={() => setShowLogoutConfirm(false)}>
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            onClick={e => e.stopPropagation()}
            className="w-full bg-white rounded-t-3xl p-6 pb-10 shadow-2xl"
          >
            <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-6" />
            <h3 className="font-serif text-2xl text-stone-900 text-center mb-2">Sign out?</h3>
            <p className="font-sans text-sm text-stone-400 text-center mb-8">Your entries are saved locally and will be here when you return.</p>
            <button
              onClick={onLogout}
              className="w-full py-4 rounded-full font-sans font-semibold text-white mb-3"
              style={{ background: 'linear-gradient(135deg, #C07A52, #E8C97A)' }}
            >
              Yes, Sign Out
            </button>
            <button
              onClick={() => setShowLogoutConfirm(false)}
              className="w-full py-4 rounded-full font-sans font-medium text-stone-500 bg-stone-50 border border-stone-200"
            >
              Cancel
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}